import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useDataProvider, useGetIdentity, useNotify } from 'react-admin';
import { AdminUser, AdminUserRole } from './adminUsersDataProvider';

const ROLES: AdminUserRole[] = [
    'manager-admin',
    'moderator',
    'moderator-admin',
    'expert',
    'expert-admin',
    'expert-outstaff',
    'expert-admin-outstaff',
    'developer'
];

const ROLE_LABELS: Record<AdminUserRole, string> = {
    'manager-admin': 'manager-admin (Суперпользователь)',
    'moderator': 'moderator (Модератор)',
    'moderator-admin': 'moderator-admin (Главный модератор)',
    'expert': 'expert (Эксперт)',
    'expert-admin': 'expert-admin (Главный эксперт)',
    'expert-outstaff': 'expert-outstaff (Внешний эксперт)',
    'expert-admin-outstaff': 'expert-admin-outstaff (Внешний менеджер эксперт)',
    'developer': 'developer (Разработчик)'
};

const LOGIN_REGEX = /^[a-zA-Z0-9._-]+$/;

const validatePasswordPolicy = (password: string) => {
    if (password.length < 10) {
        return 'Пароль должен содержать минимум 10 символов';
    }
    if (!/[A-Z]/.test(password)) {
        return 'Пароль должен содержать минимум 1 заглавную букву';
    }
    if (!/[a-z]/.test(password)) {
        return 'Пароль должен содержать минимум 1 строчную букву';
    }
    if (!/[0-9]/.test(password)) {
        return 'Пароль должен содержать минимум 1 цифру';
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
        return 'Пароль должен содержать минимум 1 спецсимвол';
    }
    return null;
};

const resolveApiErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === 'object' && error && 'status' in error) {
        const status = (error as { status?: unknown }).status;
        if (status === 409) {
            return 'Пользователь с таким login уже существует';
        }
    }

    if (typeof error === 'object' && error && 'body' in error) {
        const body = (error as { body?: unknown }).body;
        if (typeof body === 'object' && body && 'error' in body) {
            const backendError = (body as { error?: unknown }).error;
            if (typeof backendError === 'string' && backendError.trim()) {
                return backendError;
            }
        }
    }
    if (error instanceof Error && error.message.trim()) {
        const jsonMatch = error.message.match(/\{.*\}$/);
        if (jsonMatch) {
            try {
                const parsed = JSON.parse(jsonMatch[0]) as { error?: unknown };
                if (typeof parsed.error === 'string' && parsed.error.trim()) {
                    if (parsed.error === 'Login already exists') {
                        return 'Пользователь с таким login уже существует';
                    }
                    return parsed.error;
                }
            } catch (_parseError) {
                // ignore parsing errors and return the original message below
            }
        }
        return error.message;
    }
    return fallback;
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) {
        return 'Никогда';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'Неверная дата';
    }
    return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

const randomInt = (maxExclusive: number) => {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return array[0] % maxExclusive;
    }

    return Math.floor(Math.random() * maxExclusive);
};

const generateStrongPassword = (length = 20) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const special = '!@#$%^&*()_+-=';
    const all = upper + lower + digits + special;
    const randomChar = (alphabet: string) => alphabet[randomInt(alphabet.length)];

    const seed = [
        randomChar(upper),
        randomChar(lower),
        randomChar(digits),
        randomChar(special)
    ];

    while (seed.length < length) {
        seed.push(randomChar(all));
    }

    for (let index = seed.length - 1; index > 0; index -= 1) {
        const randomIndex = randomInt(index + 1);
        const current = seed[index];
        seed[index] = seed[randomIndex];
        seed[randomIndex] = current;
    }

    return seed.join('');
};

export const AdminUsersPage = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as { id?: string; role?: string } | null;
    const isManagerAdmin = currentUser?.role === 'manager-admin';

    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
    const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
    const [login, setLogin] = useState('');
    const [fullName, setFullName] = useState('');
    const [role, setRole] = useState<AdminUserRole>('moderator');
    const [password, setPassword] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await dataProvider.getList('adminUsers', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            });
            setUsers((response?.data as AdminUser[]) ?? []);
        } catch (error) {
            notify(resolveApiErrorMessage(error, 'Не удалось загрузить пользователей'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify]);

    useEffect(() => {
        if (isManagerAdmin) {
            loadUsers();
            return;
        }
        setLoading(false);
    }, [isManagerAdmin, loadUsers]);

    const resetForm = () => {
        setEditingUser(null);
        setLogin('');
        setFullName('');
        setRole('moderator');
        setPassword('');
        setIsActive(true);
        setShowPassword(false);
    };

    const openCreateDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEditDialog = (user: AdminUser) => {
        setEditingUser(user);
        setLogin(user.login);
        setFullName(user.fullName);
        setRole(user.role);
        setPassword('');
        setIsActive(user.isActive);
        setShowPassword(false);
        setDialogOpen(true);
    };

    const handleGeneratePassword = () => {
        const generatedPassword = generateStrongPassword(20);
        setPassword(generatedPassword);
        notify('Пароль сгенерирован', { type: 'info' });
    };

    const closeDialog = () => {
        if (saving) {
            return;
        }
        setDialogOpen(false);
        resetForm();
    };

    const handleSave = async () => {
        const trimmedLogin = login.trim();
        const trimmedFullName = fullName.trim();

        if (!trimmedLogin) {
            notify('Введите login', { type: 'warning' });
            return;
        }
        if (!LOGIN_REGEX.test(trimmedLogin) || trimmedLogin.length < 3 || trimmedLogin.length > 50) {
            notify('Login должен содержать 3-50 символов: буквы, цифры, ".", "_" или "-"', { type: 'warning' });
            return;
        }
        if (!trimmedFullName) {
            notify('Введите ФИО', { type: 'warning' });
            return;
        }

        const trimmedPassword = password.trim();
        if (!editingUser && !trimmedPassword) {
            notify('Введите пароль', { type: 'warning' });
            return;
        }
        if (trimmedPassword) {
            const passwordError = validatePasswordPolicy(trimmedPassword);
            if (passwordError) {
                notify(passwordError, { type: 'warning' });
                return;
            }
        }

        setSaving(true);
        try {
            if (editingUser) {
                await dataProvider.update('adminUsers', {
                    id: editingUser.id,
                    data: {
                        login: trimmedLogin,
                        fullName: trimmedFullName,
                        role,
                        isActive,
                        ...(trimmedPassword ? { password: trimmedPassword } : {})
                    },
                    previousData: editingUser
                });
                notify('Пользователь обновлен', { type: 'info' });
            } else {
                await dataProvider.create('adminUsers', {
                    data: {
                        login: trimmedLogin,
                        fullName: trimmedFullName,
                        role,
                        isActive,
                        password: trimmedPassword
                    }
                });
                notify('Пользователь создан', { type: 'info' });
            }
            setDialogOpen(false);
            resetForm();
            await loadUsers();
        } catch (error) {
            notify(resolveApiErrorMessage(error, 'Не удалось сохранить пользователя'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const closeDeleteDialog = () => {
        if (saving) {
            return;
        }
        setDeleteDialogOpen(false);
        setDeletingUser(null);
    };

    const handleDelete = async () => {
        if (!deletingUser) {
            return;
        }
        setSaving(true);
        try {
            await dataProvider.delete('adminUsers', { id: deletingUser.id, previousData: deletingUser });
            notify('Пользователь удален', { type: 'info' });
            closeDeleteDialog();
            await loadUsers();
        } catch (error) {
            notify(resolveApiErrorMessage(error, 'Не удалось удалить пользователя'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const usersTotalLabel = useMemo(() => `Всего пользователей: ${users.length}`, [users.length]);

    if (isPending || loading) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!isManagerAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Доступ только для роли manager-admin</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4">Управление пользователями</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {usersTotalLabel}
                    </Typography>
                </Box>
                <IconButton color="primary" onClick={openCreateDialog} aria-label="Добавить пользователя">
                    <AddCircleOutlineIcon />
                </IconButton>
            </Stack>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Login</TableCell>
                            <TableCell>ФИО</TableCell>
                            <TableCell>Роль</TableCell>
                            <TableCell>Последний вход</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell align="right">Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell sx={{ maxWidth: 180 }}>{user.id}</TableCell>
                                <TableCell>{user.login}</TableCell>
                                <TableCell>{user.fullName}</TableCell>
                                <TableCell>{ROLE_LABELS[user.role] ?? user.role}</TableCell>
                                <TableCell>{formatDateTime(user.lastLoginAt)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={user.isActive ? 'Активен' : 'Не активен'}
                                        color={user.isActive ? 'success' : 'default'}
                                        variant={user.isActive ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEditDialog(user)} aria-label="Редактировать">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    {user.id !== currentUser?.id ? (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                setDeletingUser(user);
                                                setDeleteDialogOpen(true);
                                            }}
                                            aria-label="Удалить"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Login"
                            value={login}
                            onChange={(event) => setLogin(event.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="ФИО"
                            value={fullName}
                            onChange={(event) => setFullName(event.target.value)}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="admin-user-role-label">Роль</InputLabel>
                            <Select
                                labelId="admin-user-role-label"
                                value={role}
                                label="Роль"
                                onChange={(event) => setRole(event.target.value as AdminUserRole)}
                            >
                                {ROLES.map((roleValue) => (
                                    <MenuItem key={roleValue} value={roleValue}>
                                        {ROLE_LABELS[roleValue]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label={editingUser ? 'Новый пароль (необязательно)' : 'Пароль'}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            type={showPassword ? 'text' : 'password'}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={handleGeneratePassword}
                                                onMouseDown={(event) => event.preventDefault()}
                                                aria-label="Сгенерировать пароль"
                                            >
                                                <AutoFixHighIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                onMouseDown={(event) => event.preventDefault()}
                                                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            fullWidth
                            helperText="Минимум 10 символов, заглавная/строчная буква, цифра и спецсимвол"
                        />
                        <FormControlLabel
                            control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />}
                            label={isActive ? 'Активен' : 'Не активен'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Отмена</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>Удалить пользователя?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Пользователь будет деактивирован и скрыт из списка.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={saving}>Отмена</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
                        {saving ? 'Удаление...' : 'Удалить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
